import { useCallback, useState } from 'react';

import type { JobProof } from '../utils/proof-labels';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseProofViewDialogReturn {
  /**
   * `Dialog`-compatible open-change handler — clears the selected pair when
   * Radix requests a close (`open === false`), no-ops otherwise. Pass
   * directly as `ProofViewDialog`'s `onOpenChange` prop.
   */
  onOpenChange: (open: boolean) => void;
  /** Opens the proof view dialog for the given job/proof pair. */
  openProofView: (pair: JobProof) => void;
  /** The job/proof pair currently shown in the dialog, or null when closed. */
  selectedProof: JobProof | null;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Owns the open/close state for the shared `ProofViewDialog`, used identically
 * by `OrderProofsTab` and `TaskProofsSection` — factored out to avoid
 * duplicating the same `useState` wiring in both entry points.
 */
export function useProofViewDialog(): UseProofViewDialogReturn {
  const [selectedProof, setSelectedProof] = useState<JobProof | null>(null);

  const onOpenChange = useCallback((open: boolean): void => {
    if (!open) setSelectedProof(null);
  }, []);

  const openProofView = useCallback((pair: JobProof): void => {
    setSelectedProof(pair);
  }, []);

  return {
    onOpenChange,
    openProofView,
    selectedProof,
  };
}
